import React, { useEffect } from "react";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Mail,
  MapPin,
  MessageSquare,
  Handshake,
  Calendar,
  Send,
  Clock,
  Globe,
  Facebook,
  Linkedin,
  Youtube,
} from "lucide-react";
import Footer from "../../components/landing/Footer";

// Custom X (Twitter) Icon Component - Official X logo shape
const XIcon = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

// Custom TikTok Icon
const TikTokIcon = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

// Custom Instagram Icon
const InstagramIcon = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
  </svg>
);

const ContactUsMediaEvents = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBack = () => {
    window.history.back();
  };

  const contactCategories = [
    {
      icon: Mail,
      title: "General Enquiries",
      email: "info@tripsecuregh.com",
      gradient: "from-orange-500 to-orange-600",
      bg: "from-orange-50 to-orange-100",
    },
    {
      icon: Handshake,
      title: "Partnerships & Sponsorships",
      email: "info@tripsecuregh.com",
      gradient: "from-blue-500 to-blue-600",
      bg: "from-blue-50 to-blue-100",
    },
    {
      icon: Calendar,
      title: "Events & Exhibitions",
      email: "info@tripsecuregh.com",
      gradient: "from-emerald-500 to-emerald-600",
      bg: "from-emerald-50 to-emerald-100",
    },
  ];
  return (
    <div className="min-h-screen bg-gradient-to-br via-white to-orange-50 from-slate-50">
      {/* Back Button */}
      <div className="fixed left-4 top-24 z-50">
        <motion.button
          onClick={handleBack}
          whileHover={{ scale: 1.05, x: -5 }}
          whileTap={{ scale: 0.95 }}
          className="flex gap-2 items-center px-4 py-2.5 rounded-xl border border-gray-200 shadow-lg backdrop-blur-md transition-all duration-300 bg-white/95 hover:bg-white hover:shadow-2xl"
        >
          <ArrowLeft className="w-4 h-4 text-gray-600" />
          <span className="text-sm font-semibold text-gray-700">Back</span>
        </motion.button>
      </div>

      {/* Hero Section */}
      <section className="pt-20 md:pt-24 lg:pt-28 pb-20 md:pb-24 lg:pb-28 overflow-hidden relative h-[550px] mt-16 md:mt-20">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1556761175-b413da4baf72?q=80&w=2074&auto=format&fit=crop"
            alt="Contact Us"
            className="object-cover w-full h-full"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-orange-900/90 via-purple-900/85 to-blue-900/90" />
        </div>

        {/* Animated particles */}
        <div className="overflow-hidden absolute inset-0">
          {[...Array(30)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-white rounded-full"
              animate={{
                y: [0, -100],
                x: [0, Math.random() * 50 - 25],
                opacity: [0, 0.5, 0],
                scale: [0, 1, 0],
              }}
              transition={{
                duration: Math.random() * 3 + 2,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
              style={{
                left: `${Math.random() * 100}%`,
                bottom: "0%",
              }}
            />
          ))}
        </div>

        <div className="flex relative z-10 justify-center items-center px-6 py-12 mx-auto max-w-6xl h-full sm:px-8 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="w-full text-center"
          >
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-flex gap-2 items-center px-6 py-3 mb-8 rounded-full border shadow-xl backdrop-blur-md md:mb-10 bg-white/20 border-white/30"
            >
              <MessageSquare className="w-5 h-5 text-orange-300" />
              <span className="text-sm font-bold tracking-wide text-white uppercase">
                Contact Us – Media & Events
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="mb-10 text-5xl font-black leading-tight text-white md:mb-12 md:text-6xl lg:text-7xl"
            >
              We'd love to
              <span className="block mt-3 text-transparent bg-clip-text bg-gradient-to-r from-orange-300 via-yellow-300 to-orange-400 md:mt-4">
                hear from you.
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="mx-auto mb-12 max-w-3xl text-xl leading-relaxed text-gray-100 md:mb-14 md:text-2xl"
            >
              Whether you're interested in media partnerships, event
              sponsorships, speaking opportunities, exhibitions, or content
              collaborations, the TripSecure Media & Events team is ready to
              support you.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Get in Touch Section */}
      <section className="px-4 py-20 -mt-20">
        <div className="mx-auto max-w-7xl sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-16 text-center"
          >
            <h2 className="mb-4 text-4xl font-black text-gray-900 md:text-5xl">
              Get in Touch
            </h2>
            <div className="mx-auto w-24 h-1.5 bg-gradient-to-r from-orange-500 to-blue-600 rounded-full" />
          </motion.div>

          <div className="grid grid-cols-1 gap-6 mb-16 md:grid-cols-3">
            {contactCategories.map((category, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                whileHover={{ y: -8, scale: 1.02 }}
                className={`relative overflow-hidden bg-gradient-to-br ${category.bg} rounded-3xl border border-gray-200 shadow-xl transition-all hover:shadow-2xl group`}
              >
                {/* Hover gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-br to-transparent opacity-0 transition-opacity duration-300 from-white/50 group-hover:opacity-100" />

                <div className="relative p-8">
                  <motion.div
                    whileHover={{ rotate: 360, scale: 1.1 }}
                    transition={{ duration: 0.6 }}
                    className={`inline-flex justify-center items-center mb-6 w-16 h-16 bg-gradient-to-br ${category.gradient} rounded-2xl shadow-xl`}
                  >
                    <category.icon className="w-8 h-8 text-white" />
                  </motion.div>
                  <h3 className="mb-4 text-xl font-bold text-gray-900">
                    {category.title}
                  </h3>
                  <a
                    href={`mailto:${category.email}`}
                    className={`inline-flex gap-2 items-center font-semibold text-gray-700 transition-all hover:gap-3 hover:text-gray-900`}
                  >
                    {category.email}
                    <Send className="w-4 h-4" />
                  </a>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Office Location */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-1 gap-8 lg:grid-cols-2"
          >
            {/* Location Card */}
            <div className="overflow-hidden relative p-8 bg-white rounded-3xl border border-gray-200 shadow-xl lg:p-10">
              <div className="flex absolute -top-6 -right-6 justify-center items-center w-32 h-32 bg-gradient-to-br rounded-full blur-2xl from-purple-400/20 to-blue-400/20" />

              <div className="relative">
                <div className="flex gap-4 items-start mb-8">
                  <div className="flex justify-center items-center w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl shadow-xl">
                    <MapPin className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="mb-2 text-2xl font-black text-gray-900">
                      Office Location
                    </h3>
                    <p className="text-base text-gray-600">
                      Visit us at our headquarters
                    </p>
                  </div>
                </div>

                <div className="p-6 mb-6 bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl border border-purple-100">
                  <p className="mb-2 text-xl font-bold text-gray-900">
                    Dokua Plaza
                  </p>
                  <p className="text-lg font-semibold text-gray-700">
                    Accra, Ghana
                  </p>
                </div>

                {/* Office Hours */}
                <div className="flex gap-3 items-start p-5 bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl border border-orange-100">
                  <Clock className="flex-shrink-0 mt-1 w-6 h-6 text-orange-600" />
                  <div>
                    <p className="mb-2 text-sm font-bold text-gray-900 uppercase">
                      Office Hours
                    </p>
                    <p className="text-sm font-semibold text-gray-700">
                      Monday – Saturday
                    </p>
                    <p className="text-sm text-gray-600">
                      8:00 AM – 4:00 PM (GMT)
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Map Placeholder */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="overflow-hidden relative bg-gray-200 rounded-3xl shadow-xl group"
            >
              <img
                src="https://images.unsplash.com/photo-1524661135-423995f22d0b?q=80&w=2074&auto=format&fit=crop"
                alt="Accra Ghana"
                className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-110"
              />
              <div className="flex absolute inset-0 justify-center items-center bg-gradient-to-br from-orange-900/60 to-blue-900/60">
                <div className="text-center">
                  <MapPin className="mx-auto mb-4 w-16 h-16 text-white" />
                  <p className="text-2xl font-bold text-white">
                    Dokua Plaza, Accra
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Social Media Section */}
      <section className="px-4 py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="mx-auto max-w-4xl sm:px-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="overflow-hidden relative p-12 text-center bg-gradient-to-br from-emerald-50 via-teal-50 to-blue-50 rounded-3xl border border-emerald-100 shadow-xl"
          >
            {/* Decorative elements */}
            <div className="flex absolute -top-8 -right-8 justify-center items-center w-32 h-32 bg-gradient-to-br rounded-full blur-2xl from-emerald-400/20 to-teal-400/20" />
            <div className="flex absolute -bottom-8 -left-8 justify-center items-center w-32 h-32 bg-gradient-to-br rounded-full blur-2xl from-blue-400/20 to-emerald-400/20" />

            <div className="relative z-10">
              <div className="inline-flex justify-center items-center mx-auto mb-6 w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl shadow-xl">
                <Globe className="w-8 h-8 text-white" />
              </div>

              <h3 className="mb-4 text-3xl font-black text-gray-900">
                Social Media
              </h3>
              <p className="mx-auto mb-8 max-w-2xl text-lg text-gray-700">
                Follow TripSecure on all major platforms for updates on upcoming
                shows, events, and opportunities.
              </p>

              {/* Social Media Icons */}
              <div className="flex flex-wrap gap-4 justify-center">
                {[
                  {
                    name: "Facebook",
                    icon: Facebook,
                    url: "https://www.facebook.com/Tripsecureofficial",
                    color: "from-blue-600 to-blue-700",
                    hoverColor: "hover:from-blue-700 hover:to-blue-800",
                  },
                  {
                    name: "X (Twitter)",
                    icon: XIcon,
                    url: "https://x.com/tripsecureofficial",
                    color: "from-gray-900 to-black",
                    hoverColor: "hover:from-black hover:to-gray-900",
                  },
                  {
                    name: "YouTube",
                    icon: Youtube,
                    url: "https://www.youtube.com/@Tripsecureofficial",
                    color: "from-red-600 to-red-700",
                    hoverColor: "hover:from-red-700 hover:to-red-800",
                  },
                  {
                    name: "TikTok",
                    icon: TikTokIcon,
                    url: "https://www.tiktok.com/@tripsecureofficial",
                    color: "from-gray-900 to-black",
                    hoverColor: "hover:from-black hover:to-gray-800",
                  },
                  {
                    name: "Instagram",
                    icon: InstagramIcon,
                    url: "https://www.instagram.com/Tripsecure_Official",
                    color: "from-purple-600 via-pink-600 to-orange-500",
                    hoverColor: "hover:from-purple-700 hover:via-pink-700 hover:to-orange-600",
                  },
                  {
                    name: "LinkedIn",
                    icon: Linkedin,
                    url: "https://www.linkedin.com/company/tripsecure-gh",
                    color: "from-blue-700 to-blue-800",
                    hoverColor: "hover:from-blue-800 hover:to-blue-900",
                  },
                ].map((social, idx) => (
                  <motion.a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, scale: 0 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    whileHover={{ scale: 1.15, y: -5 }}
                    whileTap={{ scale: 0.95 }}
                    className={`flex justify-center items-center w-14 h-14 bg-gradient-to-br ${social.color} rounded-xl shadow-lg transition-all hover:shadow-2xl ${social.hoverColor} group`}
                    title={social.name}
                  >
                    <social.icon className="w-6 h-6 text-white transition-transform group-hover:rotate-12" />
                  </motion.a>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Closing Statement */}
      <section className="px-4 py-20">
        <div className="mx-auto max-w-6xl sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="overflow-hidden relative p-12 text-center rounded-3xl shadow-2xl lg:p-20"
            style={{
              background:
                "linear-gradient(135deg, #f97316 0%, #ea580c 25%, #dc2626 50%, #9333ea 75%, #3b82f6 100%)",
            }}
          >
            {/* Animated background */}
            <motion.div
              animate={{
                scale: [1, 1.2, 1],
                rotate: [0, 180, 0],
              }}
              transition={{ duration: 20, repeat: Infinity }}
              className="absolute top-0 right-0 w-80 h-80 bg-white rounded-full opacity-10 blur-3xl"
            />

            <div className="relative z-10">
              <motion.div
                animate={{
                  scale: [1, 1.1, 1],
                }}
                transition={{ duration: 2, repeat: Infinity }}
                className="inline-flex justify-center items-center mx-auto mb-8 w-20 h-20 rounded-full border-4 shadow-2xl backdrop-blur-md bg-white/20 border-white/40"
              >
                <MessageSquare className="w-10 h-10 text-white" />
              </motion.div>

              <p className="text-3xl italic font-black text-white md:text-4xl lg:text-5xl">
                Your safety story matters.
                <span className="block mt-4 text-transparent bg-clip-text bg-gradient-to-r from-yellow-200 via-orange-200 to-yellow-200">
                  Let's tell it together.
                </span>
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactUsMediaEvents;
